import { useMutation, useQuery } from '@tanstack/react-query'
import {
    listDomainIntelligenceArtifacts,
    getDomainIntelligenceArtifactReport,
    generateDomainIntelligenceArtifactDocx,
} from './domainIntelligenceApi'

export const useDomainIntelligenceArtifacts = (workspaceID, runPath) => {
    const {
        data: artifactRunsData,
        isLoading: loadingArtifactRuns,
        error: artifactRunsError,
        refetch: refetchArtifactRuns,
    } = useQuery({
        queryKey: ['domainIntelligenceArtifacts', workspaceID],
        queryFn: () => listDomainIntelligenceArtifacts(workspaceID),
        enabled: Boolean(workspaceID),
    })

    const {
        data: reportData,
        isLoading: loadingReport,
        error: reportError,
    } = useQuery({
        queryKey: ['domainIntelligenceArtifactReport', workspaceID, runPath],
        queryFn: () => getDomainIntelligenceArtifactReport(workspaceID, runPath),
        enabled: Boolean(workspaceID) && Boolean(runPath),
    })

    const { mutate: generateArtifactDocx, isPending: isGeneratingArtifactDocx } = useMutation({
        mutationFn: ({ runPath: mutRunPath, resynthesize }) =>
            generateDomainIntelligenceArtifactDocx(workspaceID, mutRunPath, resynthesize),
        onSuccess: (response, variables) => {
            if (variables?.onSuccess) {
                variables.onSuccess(response)
            }
        },
        onError: (error, variables) => {
            if (variables?.onError) {
                variables.onError(error)
            }
        },
    })

    // The backend caps the listing (default 30 runs, hierarchy hydrated for 8)
    // and reports what it dropped; surface those counters so the UI can say
    // "showing N of M" instead of silently truncating (audit F9).
    const artifactRunLimits = artifactRunsData
        ? {
              artifactRunCount: artifactRunsData.artifactRunCount ?? null,
              maxArtifactRuns: artifactRunsData.maxArtifactRuns ?? null,
              maxHierarchyRuns: artifactRunsData.maxHierarchyRuns ?? null,
              hydratedHierarchyRuns: artifactRunsData.hydratedHierarchyRuns ?? null,
          }
        : null

    return {
        artifactRuns: artifactRunsData?.artifactRuns || [],
        artifactRunLimits,
        loadingArtifactRuns,
        artifactRunsError,
        refetchArtifactRuns,
        report: reportData || null,
        loadingReport,
        reportError,
        generateArtifactDocx,
        isGeneratingArtifactDocx,
    }
}
